import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { useBodyScrollLock } from '@client/hooks/useBodyScrollLock';

/**
 * MobileSheetBase - Foundation component for mobile slide-up sheets
 */
const MobileSheetBase = ({
  isOpen,
  onClose,
  title,
  onDone,
  showDone = false,
  doneLabel = 'Terminé',
  doneDisabled = false,
  children,
}) => {
  const [shouldRender, setShouldRender] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const closeTimerRef = useRef(null);
  const rafRef = useRef(null);

  useBodyScrollLock(isVisible === true);

  useEffect(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (isOpen) {
      setShouldRender(true);
      setIsVisible(false);
      rafRef.current = requestAnimationFrame(() => {
        rafRef.current = requestAnimationFrame(() => {
          setIsVisible(true);
        });
      });
    } else {
      setIsVisible(false);
      closeTimerRef.current = setTimeout(() => {
        setShouldRender(false);
      }, 300);
    }

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isOpen]);

  const handleBackdropClick = useCallback(
    (e) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!shouldRender) {
    return null;
  }

  return createPortal(
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black z-[9998] transition-opacity duration-300 ${
          isVisible ? 'opacity-50' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleBackdropClick}
      />

      {/* Sheet */}
      <div
        className={`fixed inset-x-0 bottom-0 h-[100dvh] bg-white rounded-t-2xl z-[9999] flex flex-col transition-transform duration-300 ${
          isVisible ? 'translate-y-0' : 'translate-y-full'
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-sheet-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <h2
            id="mobile-sheet-title"
            className="text-sm font-semibold text-gray-900 normal-case"
          >
            {title}
          </h2>
          <button
            type="button"
            className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-gray-500"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        {/* Content */}
        {children}

        {/* Footer */}
        {showDone && (
          <div className="px-4 py-3 border-t border-gray-100 bg-white flex-shrink-0">
            <button
              type="button"
              className={`w-full h-11 rounded-lg text-white text-sm font-semibold ${
                doneDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-[#105235]'
              }`}
              onClick={onDone}
              disabled={doneDisabled}
            >
              {doneLabel}
            </button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

MobileSheetBase.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  onDone: PropTypes.func,
  showDone: PropTypes.bool,
  doneLabel: PropTypes.string,
  doneDisabled: PropTypes.bool,
  children: PropTypes.node,
};

export default React.memo(MobileSheetBase);
